import {
    LENS_CATALOG_SECTION_KEYS,
    type LensCatalogData,
    type LensCatalogItem,
    type LensCatalogSection,
} from './scenarioLensCatalog.types';

export { LENS_CATALOG_SECTION_KEYS };

export const cloneLensCatalog = (catalog: LensCatalogData): LensCatalogData =>
    JSON.parse(JSON.stringify(catalog)) as LensCatalogData;

const normalizeSectionKey = (sectionKey: string) => sectionKey.trim().toLowerCase();

const getCatalogSection = (catalog: LensCatalogData, sectionKey: string): LensCatalogSection | undefined =>
    catalog.sections.find((section) => normalizeSectionKey(section.section_key) === normalizeSectionKey(sectionKey));

export const getCatalogSectionItems = (catalog: LensCatalogData, sectionKey: string): LensCatalogItem[] =>
    getCatalogSection(catalog, sectionKey)?.groups.flatMap((group) => group.items) ?? [];

export const getDimensionsSection = (catalog: LensCatalogData): LensCatalogSection | undefined =>
    getCatalogSection(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS);

export const getKpisSection = (catalog: LensCatalogData): LensCatalogSection | undefined =>
    getCatalogSection(catalog, LENS_CATALOG_SECTION_KEYS.KPIS);

export const getSystemColumnItems = (catalog: LensCatalogData): LensCatalogItem[] =>
    getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.SYSTEM_COLUMNS);

export const getCatalogSelectionSummary = (items: LensCatalogItem[], maxSelectableCount?: number) => {
    const selectableItems = items.filter((item) => !item.is_disabled);
    const selectedCount = selectableItems.filter((item) => item.is_selected).length;
    const totalCount =
        maxSelectableCount != null
            ? Math.min(selectableItems.length, maxSelectableCount)
            : selectableItems.length;

    return {
        selectedCount,
        totalCount,
        label: `${selectedCount}/${totalCount}`,
    };
};

export const getDimensionSelectionLimitState = (items: LensCatalogItem[]) => {
    const selectableItems = items.filter((item) => !item.is_disabled);
    const selectedCount = selectableItems.filter((item) => item.is_selected).length;
    const isLimitReached = selectedCount >= DEFAULT_LENS_DIMENSION_SELECTION_COUNT;

    return {
        selectedCount,
        isLimitReached,
    };
};

export const shouldBlockDimensionSelection = (item: LensCatalogItem, isLimitReached: boolean): boolean =>
    !item.is_disabled && !item.is_selected && isLimitReached;

export const hasCatalogLensSelection = (catalog: LensCatalogData): boolean => {
    const selectableItems = [
        ...getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS),
        ...getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.KPIS),
        ...getSystemColumnItems(catalog),
    ].filter((item) => !item.is_disabled);

    return selectableItems.some((item) => item.is_selected);
};

const getSelectedSystemColumns = (catalog: LensCatalogData): LensCatalogItem[] =>
    getSystemColumnItems(catalog).filter((item) => item.is_selected && !item.is_disabled);

export const isTableVisibleCatalogItem = (item: LensCatalogItem): boolean =>
    item.is_selected && !item.is_disabled;

export type SelectedTableColumns = {
    dimensions: LensCatalogItem[];
    kpis: LensCatalogItem[];
};

export const getSelectedTableColumns = (catalog: LensCatalogData): SelectedTableColumns => ({
    dimensions: getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS).filter(
        isTableVisibleCatalogItem,
    ),
    kpis: getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.KPIS).filter(isTableVisibleCatalogItem),
});

export const hasSelectedTableColumns = (catalog: LensCatalogData): boolean => {
    const { dimensions, kpis } = getSelectedTableColumns(catalog);
    return dimensions.length > 0 || kpis.length > 0;
};

export const toggleCatalogItemSelection = (
    catalog: LensCatalogData,
    catalogId: number,
    isSelected: boolean,
): LensCatalogData => {
    if (isSelected) {
        const dimensionItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS);
        const targetDimension = dimensionItems.find((item) => item.catalog_id === catalogId);
        if (
            targetDimension &&
            !targetDimension.is_disabled &&
            !targetDimension.is_selected &&
            dimensionItems.filter(isTableVisibleCatalogItem).length >= DEFAULT_LENS_DIMENSION_SELECTION_COUNT
        ) {
            return catalog;
        }
    }

    return {
        sections: catalog.sections.map((section) => ({
            ...section,
            groups: section.groups.map((group) => ({
                ...group,
                items: group.items.map((item) =>
                    item.catalog_id === catalogId
                        ? {
                              ...item,
                              is_selected: isSelected,
                              is_display_enabled: isSelected ? true : null,
                          }
                        : item,
                ),
            })),
        })),
    };
};

export const DEFAULT_LENS_DIMENSION_SELECTION_COUNT = 5;

export const LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP = `Up to ${DEFAULT_LENS_DIMENSION_SELECTION_COUNT} dimensions allowed`;

const normalizeSectionKeyForMatch = (sectionKey: string) => sectionKey.trim().toLowerCase();

const isDimensionsSectionKey = (sectionKey: string) =>
    normalizeSectionKeyForMatch(sectionKey) === normalizeSectionKeyForMatch(LENS_CATALOG_SECTION_KEYS.DIMENSIONS);

const isKpisSectionKey = (sectionKey: string) =>
    normalizeSectionKeyForMatch(sectionKey) === normalizeSectionKeyForMatch(LENS_CATALOG_SECTION_KEYS.KPIS);

const setCatalogItemSelected = (item: LensCatalogItem, isSelected: boolean): LensCatalogItem => ({
    ...item,
    is_selected: isSelected,
    is_display_enabled: isSelected ? true : null,
});

const setSectionItemsSelection = (
    catalog: LensCatalogData,
    isSelected: boolean,
    matchSection: (key: string) => boolean,
): LensCatalogData => ({
    sections: catalog.sections.map((section) => ({
        ...section,
        groups: section.groups.map((group) => ({
            ...group,
            items: group.items.map((item) => {
                if (!matchSection(section.section_key) || item.is_disabled) {
                    return item;
                }

                return setCatalogItemSelected(item, isSelected);
            }),
        })),
    })),
});

/** Select or clear every selectable KPI in the catalog. */
export const setKpiSectionSelection = (catalog: LensCatalogData, isSelected: boolean): LensCatalogData =>
    setSectionItemsSelection(catalog, isSelected, isKpisSectionKey);

export const getLensCatalogSelectionSignature = (catalog: LensCatalogData): string =>
    catalog.sections
        .flatMap((section) => section.groups.flatMap((group) => group.items))
        .map((item) => `${item.catalog_id}:${item.is_selected ? 1 : 0}`)
        .join('|');

export const areLensCatalogSelectionsEqual = (left: LensCatalogData, right: LensCatalogData): boolean =>
    getLensCatalogSelectionSignature(left) === getLensCatalogSelectionSignature(right);

/** Default lens config: first five selectable dimensions and all selectable KPIs. */
export const resetLensCatalogToDefaultSelection = (catalog: LensCatalogData): LensCatalogData => {
    const dimensionItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS).filter(
        (item) => !item.is_disabled,
    );
    const defaultDimensionIds = new Set(
        dimensionItems.slice(0, DEFAULT_LENS_DIMENSION_SELECTION_COUNT).map((item) => item.catalog_id),
    );

    return {
        sections: catalog.sections.map((section) => ({
            ...section,
            groups: section.groups.map((group) => ({
                ...group,
                items: group.items.map((item) => {
                    if (item.is_disabled) {
                        return item;
                    }

                    if (isDimensionsSectionKey(section.section_key)) {
                        return setCatalogItemSelected(item, defaultDimensionIds.has(item.catalog_id));
                    }

                    if (isKpisSectionKey(section.section_key)) {
                        return setCatalogItemSelected(item, true);
                    }

                    return item;
                }),
            })),
        })),
    };
};

/**
 * Applies first-five-dims + all-KPIs defaults when a catalog has no selected items.
 * Use only for explicit onboarding flows (e.g. after custom-lens structure edit), not when
 * rendering scenario lens config from the API — the server may intentionally return all false.
 */
export const materializeScenarioLensCatalogIfEmpty = (catalog: LensCatalogData): LensCatalogData => {
    if (hasCatalogLensSelection(catalog)) {
        return cloneLensCatalog(catalog);
    }

    return resetLensCatalogToDefaultSelection(cloneLensCatalog(catalog));
};

export const validateLensCatalogSelection = (catalog: LensCatalogData): string | null => {
    const { dimensions, kpis } = getSelectedTableColumns(catalog);

    if (dimensions.length === 0 && kpis.length === 0) {
        const selectedSystemColumns = getSelectedSystemColumns(catalog);
        if (selectedSystemColumns.length > 0) {
            const labels = selectedSystemColumns.map((item) => item.display_name).join(', ');
            return `Unselect ${labels} to remove this lens from selection.`;
        }

        return null;
    }

    if (dimensions.length === 0) {
        return 'Select at least one dimension.';
    }

    if (kpis.length === 0) {
        return 'Select at least one KPI.';
    }

    if (dimensions.length > DEFAULT_LENS_DIMENSION_SELECTION_COUNT) {
        return `Select up to ${DEFAULT_LENS_DIMENSION_SELECTION_COUNT} dimensions.`;
    }

    const kpiKeys = new Set(kpis.map((item) => item.attribute_key));
    const hasDimensionKpiConflict = dimensions.some((item) => kpiKeys.has(item.attribute_key));
    if (hasDimensionKpiConflict) {
        return 'Remove overlapping selections between dimensions and KPIs.';
    }

    return null;
};

export const isLensCatalogApplyEnabled = (
    catalog: LensCatalogData,
    baselineCatalog?: LensCatalogData | null,
): boolean => {
    if (baselineCatalog != null && areLensCatalogSelectionsEqual(catalog, baselineCatalog)) {
        return false;
    }

    return validateLensCatalogSelection(catalog) === null;
};

export const isLensCatalogResetEnabled = (
    catalog: LensCatalogData,
    baselineCatalog?: LensCatalogData | null,
): boolean =>
    baselineCatalog != null && !areLensCatalogSelectionsEqual(catalog, baselineCatalog);

export const filterCatalogItems = (items: LensCatalogItem[], searchTerm: string): LensCatalogItem[] => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
        return items;
    }

    return items.filter((item) => item.display_name.toLowerCase().includes(normalizedSearch));
};

const normalizeSystemColumnItem = (item: unknown, index: number): LensCatalogItem => {
    const record = item as Record<string, unknown>;
    const attributeKey = String(record.system_column_key ?? record.attribute_key ?? `system_column_${index}`);

    return {
        catalog_id: typeof record.catalog_id === 'number' ? record.catalog_id : index + 1,
        attribute_id: typeof record.attribute_id === 'number' ? record.attribute_id : 0,
        attribute_key: attributeKey,
        display_name: String(record.label ?? record.display_name ?? record.column_label ?? attributeKey),
        attribute_nature: 'SYSTEM_COLUMN',
        dimension: null,
        ui_role: 'SYSTEM_COLUMN',
        is_selected: Boolean(record.is_selected),
        is_display_enabled:
            typeof record.is_display_enabled === 'boolean' || record.is_display_enabled === null
                ? (record.is_display_enabled as boolean | null)
                : null,
        is_disabled: Boolean(record.is_disabled),
        disabled_reason:
            typeof record.disabled_reason === 'string' || record.disabled_reason === null
                ? (record.disabled_reason as string | null)
                : null,
    };
};

const normalizeSystemColumnsSection = (systemColumns: unknown[]): LensCatalogSection => ({
    section_key: LENS_CATALOG_SECTION_KEYS.SYSTEM_COLUMNS,
    section_name: 'System Columns',
    groups: [
        {
            group_key: 'system_columns',
            group_name: 'System Columns',
            items: systemColumns.map((item, index) => normalizeSystemColumnItem(item, index)),
        },
    ],
});

const assignGlobalCatalogIds = (sections: LensCatalogSection[]): void => {
    let nextId = 1;
    for (const section of sections) {
        for (const group of section.groups) {
            for (const item of group.items) {
                item.catalog_id = nextId++;
            }
        }
    }
};

export const parseLensConfigResponse = (data: unknown): LensCatalogData => {
    if (!data || typeof data !== 'object') {
        throw new Error('Invalid lens config response');
    }

    const payload = data as Record<string, unknown>;
    let sectionsSource: unknown[] | undefined;

    if (payload.catalog && typeof payload.catalog === 'object') {
        const catalog = payload.catalog as { sections?: unknown[] };
        if (Array.isArray(catalog.sections)) {
            sectionsSource = catalog.sections;
        }
    }

    if (!sectionsSource && Array.isArray(payload.sections)) {
        sectionsSource = payload.sections;
    }

    const sections = sectionsSource
        ? sectionsSource.map((section, sectionIndex) => normalizeCatalogSection(section, sectionIndex))
        : [];

    if (Array.isArray(payload.system_columns) && payload.system_columns.length > 0) {
        const hasSystemColumnsSection = sections.some(
            (section) =>
                normalizeSectionKey(section.section_key) ===
                normalizeSectionKey(LENS_CATALOG_SECTION_KEYS.SYSTEM_COLUMNS),
        );

        if (!hasSystemColumnsSection) {
            sections.unshift(normalizeSystemColumnsSection(payload.system_columns));
        }
    }

    if (sections.length === 0) {
        return cloneLensCatalog({ sections: [] });
    }

    assignGlobalCatalogIds(sections);

    return cloneLensCatalog({ sections });
};

const normalizeCatalogItem = (item: unknown, index: number): LensCatalogItem => {
    const record = item as Record<string, unknown>;
    const attributeKey = String(record.attribute_key ?? record.id ?? index);

    return {
        catalog_id: typeof record.catalog_id === 'number' ? record.catalog_id : index + 1,
        attribute_id: typeof record.attribute_id === 'number' ? record.attribute_id : 0,
        attribute_key: attributeKey,
        display_name: String(record.display_name ?? record.label ?? attributeKey),
        attribute_nature: String(record.attribute_nature ?? ''),
        dimension: typeof record.dimension === 'string' ? record.dimension : null,
        ui_role: String(record.ui_role ?? ''),
        is_selected: Boolean(record.is_selected),
        is_display_enabled:
            typeof record.is_display_enabled === 'boolean' || record.is_display_enabled === null
                ? (record.is_display_enabled as boolean | null)
                : null,
        is_disabled: Boolean(record.is_disabled),
        disabled_reason:
            typeof record.disabled_reason === 'string' || record.disabled_reason === null
                ? (record.disabled_reason as string | null)
                : null,
    };
};

const normalizeCatalogSection = (section: unknown, sectionIndex: number): LensCatalogSection => {
    const record = section as Record<string, unknown>;
    const groups = Array.isArray(record.groups) ? record.groups : [];

    return {
        section_key: String(record.section_key ?? `section-${sectionIndex}`),
        section_name: String(record.section_name ?? record.section_label ?? record.section_key ?? `Section ${sectionIndex + 1}`),
        groups: groups.map((group, groupIndex) => {
            const groupRecord = group as Record<string, unknown>;
            const items = Array.isArray(groupRecord.items) ? groupRecord.items : [];

            return {
                group_key: String(groupRecord.group_key ?? `group-${groupIndex}`),
                group_name: String(groupRecord.group_name ?? groupRecord.group_key ?? `Group ${groupIndex + 1}`),
                items: items.map((item, itemIndex) => normalizeCatalogItem(item, itemIndex)),
            };
        }),
    };
};
